/**
 * Per-(resource, location) weekly schedule grid (DNKADM-0054 U2) — the editable 7-row
 * multi-shift schedule for `GET/PUT /v2/admin/resources/{id}/schedule?locationId=…`,
 * seeded from the loaded view and saved through `putResourceSchedule`.
 *
 * Mirrors the staff weekly grid (`StaffScheduleGrid`, DNKADM-0053): seven rows in
 * `dayOfWeek` order **0=Sunday … 6=Saturday** (the §5.6/§5.7 convention), each row an
 * availability toggle and, when available, 1..n shift rows — each a native
 * `<TextField type="time">` start + end (HH:mm, the LOCATION's local time) with
 * add/remove. A single **"Save schedule"** action `PUT`s all seven days at once for the
 * active location.
 *
 * Client validation mirrors the server via the SHARED `@/utils/shiftValidation`
 * (DNKADM-0053): an available day needs ≥1 shift, each shift `start < end` (overnight
 * disallowed), no overlap/duplicate. A failing day shows its inline error and BLOCKS
 * Save — no PUT reaches the wire while invalid. On a successful save the parent
 * schedule is `refetch`ed and a success toast is raised; a validation `400` renders
 * INLINE, anything else raises a non-blocking error toast (`classifyFormError`).
 *
 * Native MUI time inputs only — NO `@mui/x-date-pickers` (not in the fleet baseline).
 */
import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import {
  putResourceSchedule,
  type ResourceScheduleDay,
  type ResourceShift,
} from '@/api/resources';
import { validateShifts } from '@/utils/shiftValidation';
import { copyDay } from '@/utils/scheduleCopy';
import { CopyDayButton } from '@/components/CopyDayButton';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

/** Sun–Sat day labels indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Per-day error message, keyed by `dayOfWeek` (0=Sun … 6=Sat). */
type DayErrors = Record<number, string>;

/** A blank shift for a freshly-available day / an added shift row. */
function blankShift(): ResourceShift {
  return { startTime: '', endTime: '' };
}

/** Validate the seven days: each available day's shifts checked; closed days pass. */
function validateDays(days: ResourceScheduleDay[]): { ok: true } | { ok: false; errors: DayErrors } {
  const errors: DayErrors = {};
  for (const day of days) {
    if (!day.isAvailable) continue;
    const message = validateShifts(day.shifts);
    if (message !== null) errors[day.dayOfWeek] = message;
  }
  return Object.keys(errors).length === 0 ? { ok: true } : { ok: false, errors };
}

interface ResourceScheduleGridProps {
  /** The loaded weekly schedule (always seven days in `dayOfWeek` 0→6 order). */
  days: ResourceScheduleDay[];
  /** The resource id, forwarded on PUT. */
  resourceId: string;
  /** The active location id this schedule is authored for, forwarded on PUT. */
  locationId: string;
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

export function ResourceScheduleGrid({
  days: initialDays,
  resourceId,
  locationId,
  refetch,
  showToast,
}: ResourceScheduleGridProps) {
  // Edit the seven rows locally; the parent remounts this on each (re)load / location
  // change so the rows reseed from the freshly loaded view.
  const [days, setDays] = useState<ResourceScheduleDay[]>(initialDays);
  const [dayErrors, setDayErrors] = useState<DayErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function updateDay(dayOfWeek: number, patch: Partial<ResourceScheduleDay>) {
    setDays((prev) => prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  }

  function handleToggleAvailable(dayOfWeek: number, available: boolean) {
    updateDay(
      dayOfWeek,
      available
        ? { isAvailable: true, shifts: [blankShift()] }
        : { isAvailable: false, shifts: [] },
    );
  }

  function updateShift(dayOfWeek: number, index: number, patch: Partial<ResourceShift>) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.map((s, i) => (i === index ? { ...s, ...patch } : s)) }
          : d,
      ),
    );
  }

  function addShift(dayOfWeek: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek ? { ...d, shifts: [...d.shifts, blankShift()] } : d,
      ),
    );
  }

  function removeShift(dayOfWeek: number, index: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.filter((_s, i) => i !== index) }
          : d,
      ),
    );
  }

  // Copy one day's availability + (cloned) shifts into the chosen target days,
  // staging the change for the existing whole-week Save (no extra request).
  function copyDayTo(sourceDow: number, targetDows: number[]) {
    setDays((prev) => copyDay(prev, sourceDow, targetDows));
  }

  async function handleSave() {
    setServerError(null);
    const result = validateDays(days);
    if (!result.ok) {
      setDayErrors(result.errors);
      return;
    }
    setDayErrors({});
    setSaving(true);
    try {
      await putResourceSchedule(resourceId, locationId, days);
      refetch();
      showToast('Schedule saved', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'The schedule couldn’t be saved. Please try again.');
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
        {days.map((day, i) => (
          <DayRow
            key={day.dayOfWeek}
            label={DAY_LABELS[day.dayOfWeek] ?? `Day ${day.dayOfWeek}`}
            dayLabels={DAY_LABELS}
            day={day}
            error={dayErrors[day.dayOfWeek]}
            disabled={saving}
            last={i === days.length - 1}
            onToggleAvailable={(available) => handleToggleAvailable(day.dayOfWeek, available)}
            onShiftChange={(index, patch) => updateShift(day.dayOfWeek, index, patch)}
            onAddShift={() => addShift(day.dayOfWeek)}
            onRemoveShift={(index) => removeShift(day.dayOfWeek, index)}
            onCopyDay={(targetDows) => copyDayTo(day.dayOfWeek, targetDows)}
          />
        ))}
      </Paper>
      {serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      <Stack
        direction="row"
        sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 1.75 }}
      >
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save schedule'}
        </Button>
      </Stack>
    </Box>
  );
}

/** One day's row: the day label + availability toggle, and (when available) shifts. */
function DayRow({
  label,
  dayLabels,
  day,
  error,
  disabled,
  last,
  onToggleAvailable,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onCopyDay,
}: {
  label: string;
  dayLabels: string[];
  day: ResourceScheduleDay;
  error: string | undefined;
  disabled: boolean;
  last: boolean;
  onToggleAvailable: (available: boolean) => void;
  onShiftChange: (index: number, patch: Partial<ResourceShift>) => void;
  onAddShift: () => void;
  onRemoveShift: (index: number) => void;
  onCopyDay: (targetDows: number[]) => void;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 96, pt: 1 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={day.isAvailable}
                onChange={(e) => onToggleAvailable(e.target.checked)}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': `${label} available` } }}
              />
            }
            label={day.isAvailable ? 'Available' : 'Unavailable'}
            sx={{ mb: day.isAvailable ? 1 : 0 }}
          />
          {day.isAvailable &&
            day.shifts.map((shift, index) => (
              <Stack
                // Shift rows are positional; index keys are correct (add/remove rebuilds the list).
                key={index}
                direction="row"
                sx={{ alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}
              >
                <TextField
                  type="time"
                  label="Start"
                  size="small"
                  value={shift.startTime}
                  onChange={(e) => onShiftChange(index, { startTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} start time (HH:mm, local)`,
                    },
                  }}
                />
                <TextField
                  type="time"
                  label="End"
                  size="small"
                  value={shift.endTime}
                  onChange={(e) => onShiftChange(index, { endTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} end time (HH:mm, local)`,
                    },
                  }}
                />
                <IconAction
                  size="small"
                  label={`Remove ${label} shift ${index + 1}`}
                  onClick={() => onRemoveShift(index)}
                  disabled={disabled || day.shifts.length === 1}
                >
                  <Icon name="close" sx={{ fontSize: 18 }} />
                </IconAction>
              </Stack>
            ))}
          {day.isAvailable && (
            <Button
              size="small"
              startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
              onClick={onAddShift}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Add shift
            </Button>
          )}
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <CopyDayButton
            sourceDow={day.dayOfWeek}
            dayLabels={dayLabels}
            disabled={disabled}
            onApply={onCopyDay}
          />
        </Box>
      </Stack>
      {error && (
        <Typography role="alert" sx={{ mt: 0.75, fontSize: 12, color: 'error.main' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
